//#region Imports
import { getDiscoCompanyMenuItems } from "../../components/AdminCompanySelect/DiscoCompanyMenuItems";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  clampIsoDateToToday,
  getReportDateInputProps,
} from "../../utils/reportDateLimits";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import TextField from '../../components/SanitizedTextField/SanitizedTextField';
import Backdrop from "@mui/material/Backdrop";
import AppLoader from "../../components/AppLoader/AppLoader";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useSelector } from "react-redux";
import XLSX, { boldDomTheadRows } from "utils/xlsxStyled";
import eOpsLogo from "../../images/eops.png";
import { cfpBaseUrl } from "../../network/urls";
import {
  convertDate,
  convertDatewithTime,
  getName,
  openJsPdfInNewTab,
} from "../../services/Functions";
import "../../style/common.scss";
import ReportExportButtons from "./components/ReportExportButtons";
import "./reportPageLayout.scss";
import "./ChangeOfAttributesStatusReport.scss";
import "./PerformanceEvaluationReport.scss";
import {
  renderCircleValue,
  renderDivisionValue,
  renderSubDivisionValue,
} from "./coaHierarchySelectRenderers";
import FESCOLOGO from "../../images/DISCO Logos/FESCO.png";
import GEPCOLOGO from "../../images/DISCO Logos/GEPCO.png";
import HESCOLOGO from "../../images/DISCO Logos/HESCO.png";
import IESCOLOGO from "../../images/DISCO Logos/IESCO.png";
import MEPCOLOGO from "../../images/DISCO Logos/MEPCO.png";
import LESCOLOGO from "../../images/DISCO Logos/LESCO.png";
import PESCOLOGO from "../../images/DISCO Logos/PESCO.png";
import QESCOLOGO from "../../images/DISCO Logos/QESCO.png";
import SEPCOLOGO from "../../images/DISCO Logos/SEPCO.png";
import TESCOLOGO from "../../images/DISCO Logos/TESCO.png";
import HAZECOLOGO from "../../images/DISCO Logos/HAZECO.jpg";
import pitcLogo from "../../images/pitc.png";
import {
  getEffectiveAdminCompanyCode,
  getInitialAdminOfficeCode,
  resolveOfficeCodeForAdmin,
  resolveReportGoOfficeCode,
} from "../../utils/adminCompany";
import usePerfEvalStickyHeaderHeight from "../../hooks/usePerfEvalStickyHeaderHeight";
//#endregion

const REPORT_TITLE = "Performance Evaluation Report";

const DEFAULT_CONNECTION_TYPES = [
  "Domestic",
  "Commercial",
  "Industrial",
  "Agriculture",
  "Temporary",
  "General Services",
  "Electrification",
  "Public Lighting",
  "Corporate Company",
  "Residential Colony attached to Industry",
  "Tubewell",
  "Railway Tracking",
  "EV Charging Station",
  "Electric Vehicles Charging Station",
  "Wapda Employee",
];

const REPORT_LEAF_KEYS = [
  "applicationsReceived",
  "unRipeTotal",
  "unRipeUnderSurvey",
  "unRipeDNPaymentAwaited",
  "ripeTotal",
  "metersInstalled",
  "pendingRipe",
  "postedForBilling",
  "delayedBilling",
  "cancelApplications",
  "objected",
];

const LEAF_COLUMN_DEFS = {
  applicationsReceived: {
    count: "totalApplications",
    load: "totalLoadKW",
  },
  unRipeTotal: {
    count: "unRipeTotal",
    load: "unRipeTotalLoadKW",
  },
  unRipeUnderSurvey: {
    count: "unRipeUnderSurveyTotal",
    load: "unRipeUnderSurveyLoadKW",
  },
  unRipeDNPaymentAwaited: {
    count: "unRipeDNPaymentAwaitedTotal",
    load: "unRipeDNPaymentAwaitedLoadKW",
  },
  ripeTotal: {
    count: "totalRipeTotal",
    load: "totalRipeLoadKW",
  },
  metersInstalled: {
    count: "meterInstalledTotal",
    load: "meterInstalledLoadKW",
  },
  pendingRipe: {
    count: "pendingRipeTotal",
    load: "pendingRipeLoadKW",
  },
  postedForBilling: {
    count: "postedForBillingTotal",
    load: "postedForBillingLoadKW",
  },
  delayedBilling: {
    count: "delayedBillingTotal",
    load: "delayedBillingLoadKW",
  },
  cancelApplications: {
    count: "cancelledTotal",
    load: "cancelledLoadKW",
  },
  objected: {
    count: "objectedTotal",
    load: "objectedLoadKW",
  },
};

const PERF_EVAL_TABLE_COL_SPAN = 1 + REPORT_LEAF_KEYS.length * 2;

const STAGE_NAME_MAP = {
  "applications received": "applicationsReceived",
  "application received": "applicationsReceived",
  "applications received stage": "applicationsReceived",
  "demand notice paid": "pendingRipe",
  "pending ripe": "pendingRipe",
  "un ripe": "unRipeTotal",
  unripe: "unRipeTotal",
  "under survey": "unRipeUnderSurvey",
  "dn issued": "unRipeDNPaymentAwaited",
  "dn payment awaited": "unRipeDNPaymentAwaited",
  "meters installed": "metersInstalled",
  "meter installed": "metersInstalled",
  "posted for billing": "postedForBilling",
  "delayed billing": "delayedBilling",
  "cancel applications": "cancelApplications",
  "cancel application": "cancelApplications",
  objected: "objected",
  "objected applications": "objected",
};

const normalizeKey = (value) => (value || "").toString().trim().toLowerCase();

const CONNECTION_TYPE_CANONICAL = {
  "public lightning": "Public Lighting",
  "public lighting": "Public Lighting",
  "residential colonies": "Residential Colony attached to Industry",
  "residential colony attached to industry":
    "Residential Colony attached to Industry",
  "ev charging station": "EV Charging Station",
  "electric vehicles charging station": "Electric Vehicles Charging Station",
};

const canonicalConnectionType = (type) => {
  if (!type) return "Unknown";
  const trimmed = String(type).trim();
  return CONNECTION_TYPE_CANONICAL[normalizeKey(trimmed)] || trimmed;
};

const connectionTypeKey = (type) =>
  normalizeKey(canonicalConnectionType(type));

const dedupeConnectionTypes = (types) => {
  const seen = new Set();
  const result = [];
  (types || []).forEach((type) => {
    const canonical = canonicalConnectionType(type);
    const key = connectionTypeKey(canonical);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(canonical);
    }
  });
  return result;
};

const connectionTypeSortIndex = (type) => {
  const key = connectionTypeKey(type);
  const idx = DEFAULT_CONNECTION_TYPES.findIndex(
    (item) => connectionTypeKey(item) === key,
  );
  return idx === -1 ? DEFAULT_CONNECTION_TYPES.length : idx;
};

const sortConnectionTypeRows = (rows) =>
  [...(rows || [])].sort((a, b) => {
    const labelA = a?.label || a;
    const labelB = b?.label || b;
    const diff = connectionTypeSortIndex(labelA) - connectionTypeSortIndex(labelB);
    if (diff !== 0) return diff;
    return String(labelA).localeCompare(String(labelB));
  });

const createEmptyCell = () => ({ count: 0, load: 0 });

const buildEmptyMatrix = (connectionTypes) => {
  const matrix = {};
  connectionTypes.forEach((type) => {
    const typeKey = connectionTypeKey(type);
    matrix[typeKey] = {};
    REPORT_LEAF_KEYS.forEach((leafKey) => {
      matrix[typeKey][leafKey] = createEmptyCell();
    });
  });
  return matrix;
};

const resolveStageKey = (item) => {
  const raw =
    item.rcoProgStatusName ||
    item.encProgStatusName ||
    item.statusName ||
    item.stageName ||
    item.progStatusName ||
    item.newConProgStatusName ||
    item.performanceEvaluationStatus ||
    item.reportStatusName ||
    "";
  return STAGE_NAME_MAP[normalizeKey(raw)] || null;
};

const normalizeReportRows = (result) => {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  if (Array.isArray(result.data)) return result.data;
  if (Array.isArray(result.message)) return result.message;
  if (Array.isArray(result.result)) return result.result;
  return [];
};

const isSuccessfulReportResponse = (result) => {
  const status = result?.status;
  return (
    status === "200" ||
    status === 200 ||
    status === "success" ||
    status === true
  );
};

const resolveCount = (item) =>
  Number(
    item.applicationCount ??
      item.totalCount ??
      item.count ??
      item.noOfApplications ??
      item.numberOfApplications ??
      0,
  ) || 0;

const resolveLoad = (item) =>
  Number(
    item.loadKw ??
      item.loadKW ??
      item.totalLoad ??
      item.load ??
      item.sanctionedLoad ??
      0,
  ) || 0;

const addCellValues = (cell, count, load) => {
  cell.count += Number(count) || 0;
  cell.load += Number(load) || 0;
};

const AGGREGATED_FIELD_ALIASES = {
  unRipeTotalLoadKW: ["unRipeTotalLoadKW", "unRipeLoadKW", "unRipeLoadKw"],
  totalRipeTotal: [
    "totalRipeTotal",
    "demandNoticePaidMeterInstalledTotal",
    "demandNoticePaidAndMeterInstalledTotal",
    "ripeTotal",
    "ripeTotalCount",
  ],
  totalRipeLoadKW: [
    "totalRipeLoadKW",
    "demandNoticePaidMeterInstalledLoadKW",
    "demandNoticePaidMeterInstalledLoadKw",
    "demandNoticePaidMeterInstalledLoad",
    "demandNoticePaidAndMeterInstalledLoadKW",
    "ripeLoadKW",
    "ripeLoadKw",
  ],
  pendingRipeTotal: ["pendingRipeTotal", "demandNoticePaidTotal"],
  pendingRipeLoadKW: [
    "pendingRipeLoadKW",
    "demandNoticePaidLoadKW",
    "demandNoticePaidLoadKw",
  ],
};

const resolveAggregatedFieldValue = (item, fieldName) => {
  const aliases = AGGREGATED_FIELD_ALIASES[fieldName] || [fieldName];
  for (const alias of aliases) {
    if (item?.[alias] != null) return item[alias];
  }

  const target = String(fieldName).toLowerCase();
  const matchedKey = Object.keys(item || {}).find(
    (key) => key.toLowerCase() === target,
  );
  if (matchedKey && item[matchedKey] != null) return item[matchedKey];

  return null;
};

const resolveAggregatedLeafValues = (item, leafKey) => {
  const fields = LEAF_COLUMN_DEFS[leafKey];
  if (!fields) return { count: null, load: null };

  let countVal = resolveAggregatedFieldValue(item, fields.count);
  let loadVal = resolveAggregatedFieldValue(item, fields.load);

  if (leafKey === "unRipeTotal") {
    const surveyCount = resolveAggregatedFieldValue(item, "unRipeUnderSurveyTotal");
    const dnCount = resolveAggregatedFieldValue(item, "unRipeDNPaymentAwaitedTotal");
    const surveyLoad = resolveAggregatedFieldValue(item, "unRipeUnderSurveyLoadKW");
    const dnLoad = resolveAggregatedFieldValue(item, "unRipeDNPaymentAwaitedLoadKW");

    if (countVal == null && (surveyCount != null || dnCount != null)) {
      countVal = (Number(surveyCount) || 0) + (Number(dnCount) || 0);
    }
    if (loadVal == null && (surveyLoad != null || dnLoad != null)) {
      loadVal = (Number(surveyLoad) || 0) + (Number(dnLoad) || 0);
    }
  }

  if (leafKey === "ripeTotal") {
    const metersCount = resolveAggregatedFieldValue(item, "meterInstalledTotal");
    const pendingCount = resolveAggregatedFieldValue(item, "pendingRipeTotal");
    const metersLoad = resolveAggregatedFieldValue(item, "meterInstalledLoadKW");
    const pendingLoad = resolveAggregatedFieldValue(item, "pendingRipeLoadKW");

    if (countVal == null && (metersCount != null || pendingCount != null)) {
      countVal = (Number(metersCount) || 0) + (Number(pendingCount) || 0);
    }
    if (loadVal == null && (metersLoad != null || pendingLoad != null)) {
      loadVal = (Number(metersLoad) || 0) + (Number(pendingLoad) || 0);
    }
  }

  return { count: countVal, load: loadVal };
};

const isAggregatedReportRow = (item) =>
  Boolean(
    item &&
      (item.totalApplications != null ||
        item.unRipeTotal != null ||
        item.totalRipeTotal != null ||
        item.demandNoticePaidMeterInstalledTotal != null ||
        item.pendingRipeTotal != null ||
        item.demandNoticePaidTotal != null ||
        item.meterInstalledTotal != null ||
        item.objectedTotal != null),
  );

const resolveAggregatedRowIdentity = (item) => {
  const connectionType = item.connectionType || item.connectionTypeName;
  if (connectionType) {
    const label = canonicalConnectionType(connectionType);
    return {
      key: connectionTypeKey(label),
      label,
      dimension: "connectionType",
    };
  }

  if (item.subDivision) {
    const label = String(item.subDivision).trim();
    return { key: normalizeKey(label), label, dimension: "subDivision" };
  }
  if (item.division) {
    const label = String(item.division).trim();
    return { key: normalizeKey(label), label, dimension: "division" };
  }
  if (item.circle) {
    const label = String(item.circle).trim();
    return { key: normalizeKey(label), label, dimension: "circle" };
  }

  return {
    key: "unknown",
    label: "Unknown",
    dimension: "connectionType",
  };
};

const ROW_DIMENSION_LABELS = {
  connectionType: "Connection Type",
  subDivision: "Sub Division",
  division: "Division",
  circle: "Circle",
};

const mapAggregatedApiDataToMatrix = (data) => {
  const matrix = {};
  const displayRows = [];
  const seenKeys = new Set();
  let rowDimension = "connectionType";

  (data || []).forEach((item) => {
    const identity = resolveAggregatedRowIdentity(item);
    if (identity.dimension !== "connectionType") {
      rowDimension = identity.dimension;
    }

    if (!matrix[identity.key]) {
      matrix[identity.key] = {};
      REPORT_LEAF_KEYS.forEach((leafKey) => {
        matrix[identity.key][leafKey] = createEmptyCell();
      });
    }

    if (!seenKeys.has(identity.key)) {
      seenKeys.add(identity.key);
      displayRows.push(identity);
    }

    REPORT_LEAF_KEYS.forEach((leafKey) => {
      const { count: countVal, load: loadVal } = resolveAggregatedLeafValues(
        item,
        leafKey,
      );
      if (countVal == null && loadVal == null) return;

      addCellValues(matrix[identity.key][leafKey], countVal, loadVal);
    });
  });

  return {
    matrix,
    displayRows:
      rowDimension === "connectionType"
        ? sortConnectionTypeRows(displayRows)
        : displayRows,
    rowDimension,
  };
};

const mapLegacyApiDataToMatrix = (data, connectionTypes) => {
  const matrix = buildEmptyMatrix(connectionTypes);

  (data || []).forEach((item) => {
    const stageKey = resolveStageKey(item);
    const typeName = canonicalConnectionType(
      item.connectionType || item.connectionTypeName || "Unknown",
    );
    const typeKey = connectionTypeKey(typeName);

    if (!stageKey) return;

    if (!LEAF_COLUMN_DEFS[stageKey]) return;

    if (!matrix[typeKey]) {
      matrix[typeKey] = {};
      REPORT_LEAF_KEYS.forEach((key) => {
        matrix[typeKey][key] = createEmptyCell();
      });
    }

    matrix[typeKey][stageKey].count += resolveCount(item);
    matrix[typeKey][stageKey].load += resolveLoad(item);
  });

  return matrix;
};

const mapApiDataToMatrix = (data, connectionTypes) => {
  if ((data || []).some(isAggregatedReportRow)) {
    return mapAggregatedApiDataToMatrix(data);
  }

  return {
    matrix: mapLegacyApiDataToMatrix(data, connectionTypes),
    displayRows: [],
    rowDimension: "connectionType",
  };
};

const computeTotalsRow = (matrix, displayRows) => {
  const totals = {};
  REPORT_LEAF_KEYS.forEach((leafKey) => {
    totals[leafKey] = createEmptyCell();
  });

  displayRows.forEach(({ key }) => {
    const row = matrix[key];
    if (!row) return;

    REPORT_LEAF_KEYS.forEach((leafKey) => {
      totals[leafKey].count += row[leafKey]?.count || 0;
      totals[leafKey].load += row[leafKey]?.load || 0;
    });
  });

  return totals;
};

const formatCount = (value) => {
  const num = Math.round(Number(value) || 0);
  return num.toLocaleString("en-US");
};

const formatLoad = (value) => {
  const num = Number(value) || 0;
  if (num === 0) return "0";
  const rounded = Math.round(num * 10) / 10;
  return rounded.toLocaleString("en-US", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

const matrixHasData = (matrix, displayRows) =>
  displayRows.some(({ key }) => {
    const row = matrix[key];
    if (!row) return false;
    return REPORT_LEAF_KEYS.some(
      (leafKey) =>
        (row[leafKey]?.count || 0) > 0 || (row[leafKey]?.load || 0) > 0,
    );
  });

const PerformanceEvaluationReport = () => {
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { token, code, name, roleName, roleTypeName, cscID, cfcLevelId, selectedAdminCompanyCode } =
    useSelector((state) => state.auth);

  const companyCode = code.slice(0, 2);
  const offCode = code;

  const [viewBackdrop, setViewBackdrop] = useState(false);
  const [tableData, setTableData] = useState({});
  const [dateFrom, setDateFrom] = useState(getCurrentDate());
  const [dateTo, setDateTo] = useState(getCurrentDate());
  const tableRef = useRef(null);
  const [subDivisions, setSubDivisions] = useState(
    JSON.parse(window.localStorage.getItem("subDivisions")) || [],
  );
  const [selectedCompanyCode, setSelectedCompanyCode] = useState(
    getInitialAdminOfficeCode(code, selectedAdminCompanyCode),
  );
  const [circleCode, setCircleCode] = useState("");
  const [circleName, setCircleName] = useState("");
  const [officeCode, setOfficeCode] = useState("");
  const [officeName, setOfficeName] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedSubDivision, setSelectedSubDivision] = useState("");
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [filteredSubDivisions, setFilteredSubDivisions] = useState([]);
  const [filteredCircles, setFilteredCircles] = useState([]);
  const [hierarchy, setHierarchy] = useState("");
  const [isGoDisabled, setIsGoDisabled] = useState(true);
  const [lastAppliedFilters, setLastAppliedFilters] = useState(null);
  const [connectionTypes, setConnectionTypes] = useState(DEFAULT_CONNECTION_TYPES);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [displayRows, setDisplayRows] = useState([]);
  const [rowDimension, setRowDimension] = useState("connectionType");

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    if (String(code) === "10" && selectedAdminCompanyCode) {
      const company = String(selectedAdminCompanyCode);
      setSelectedCompanyCode(company);
      setOfficeCode(company);
    }
  }, [code, selectedAdminCompanyCode]);

  useEffect(() => {
    if (String(code) !== "10") return;
    if (circleCode || selectedDivision || selectedSubDivision) return;
    const company = getEffectiveAdminCompanyCode(
      code,
      selectedCompanyCode,
      selectedAdminCompanyCode,
    );
    if (company) {
      setOfficeCode(company);
    }
  }, [
    code,
    selectedCompanyCode,
    selectedAdminCompanyCode,
    circleCode,
    selectedDivision,
    selectedSubDivision,
  ]);

  useEffect(() => {
    if (circleCode) {
      setOfficeCode(circleCode);
    }
  }, [circleCode]);

  useEffect(() => {
    const fetchConnectionTypes = async () => {
      try {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        const response = await fetch(cfpBaseUrl + "enc/getConnectionTypes", {
          method: "POST",
          headers: myHeaders,
          body: "",
          redirect: "follow",
        });
        const result = await response.json();
        if (result.status === "200" && Array.isArray(result.data)) {
          const types = result.data
            .map((item) => item.connectionTypeName)
            .filter(Boolean);
          if (types.length > 0) {
            setConnectionTypes(types);
          }
        }
      } catch (error) {
        // keep defaults
      }
    };

    if (token) {
      fetchConnectionTypes();
    }
  }, [token]);

  useEffect(() => {
    if (code == 10 && selectedCompanyCode) {
      const fetchSubDivisions = async () => {
        setViewBackdrop(true);
        const myHeaders = new Headers();
        myHeaders.append("Authorization", token);
        const formdata = new FormData();
        formdata.append("companyCode", selectedCompanyCode);
        try {
          const response = await fetch(
            cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
            { method: "POST", headers: myHeaders, body: formdata, redirect: "follow" },
          );
          if (response.ok) {
            const result = await response.json();
            if (result.status === "200") {
              setSubDivisions(result.message);
              const circles = result.message.reduce((acc, curr) => {
                if (!acc.some((item) => item.circleCode === curr.circleCode)) {
                  acc.push({
                    circleCode: curr.circleCode,
                    circleName: curr.circleName,
                    companyCode: curr.companyCode,
                  });
                }
                return acc;
              }, []);
              setFilteredCircles(circles);
            } else {
              setSubDivisions([]);
              setFilteredCircles([]);
            }
          }
        } catch (error) {
          setSubDivisions([]);
          setFilteredCircles([]);
        } finally {
          setViewBackdrop(false);
        }
      };
      fetchSubDivisions();
    } else if (code != 10) {
      const circles = subDivisions.reduce((acc, curr) => {
        if (!acc.some((item) => item.circleCode === curr.circleCode)) {
          acc.push({
            circleCode: curr.circleCode,
            circleName: curr.circleName,
            companyCode: curr.companyCode,
          });
        }
        return acc;
      }, []);
      setFilteredCircles(circles);
    }
  }, [code, selectedCompanyCode, token]);

  const getCompanies = (company) => {
    switch (String(company)) {
      case "10":
        return "Power Information Technology Company";
      case "11":
        return "Lahore Electric Supply Company";
      case "12":
        return "Gujranwala Electric Power Company";
      case "13":
        return "Faisalabad Electric Supply Company";
      case "14":
        return "Islamabad Electric Supply Company";
      case "15":
        return "Multan Electric Power Company";
      case "26":
        return "Peshawar Electric Supply Company";
      case "27":
        return "Hazara Electric Supply Company";
      case "38":
        return "Sukkur Electric Power Company";
      case "37":
        return "Hyderabad Electric Supply Company";
      case "48":
        return "Quetta Electric Supply Company";
      case "59":
        return "Tribal Electric Supply Company";
      default:
        return "";
    }
  };

  const getLogo = (logoCode) => {
    switch (String(logoCode)) {
      case "10":
        return pitcLogo;
      case "11":
        return LESCOLOGO;
      case "12":
        return GEPCOLOGO;
      case "13":
        return FESCOLOGO;
      case "14":
        return IESCOLOGO;
      case "15":
        return MEPCOLOGO;
      case "26":
        return PESCOLOGO;
      case "27":
        return HAZECOLOGO;
      case "37":
        return HESCOLOGO;
      case "38":
        return SEPCOLOGO;
      case "48":
        return QESCOLOGO;
      case "59":
        return TESCOLOGO;
      default:
        return MEPCOLOGO;
    }
  };

  const handleChangeCompany = async (e) => {
    const selectedCompany = e.target.value;
    setSelectedCompanyCode(selectedCompany);
    setOfficeCode(selectedCompany);
    setOfficeName(getCompanies(selectedCompany) || "");
    setCircleCode("");
    setSelectedDivision("");
    setSelectedSubDivision("");
    setCircleName("");
    setFilteredDivisions([]);
    setFilteredSubDivisions([]);

    if (code == 10 && selectedCompany) {
      setViewBackdrop(true);
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      const formdata = new FormData();
      formdata.append("companyCode", selectedCompany);
      try {
        const response = await fetch(
          cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
          { method: "POST", headers: myHeaders, body: formdata, redirect: "follow" },
        );
        if (response.ok) {
          const result = await response.json();
          if (result.status === "200") {
            setSubDivisions(result.message);
            const circles = result.message.reduce((acc, curr) => {
              if (!acc.some((item) => item.circleCode === curr.circleCode)) {
                acc.push({
                  circleCode: curr.circleCode,
                  circleName: curr.circleName,
                  companyCode: curr.companyCode,
                });
              }
              return acc;
            }, []);
            setFilteredCircles(circles);
          }
        }
      } catch (error) {
        setFilteredCircles([]);
      } finally {
        setViewBackdrop(false);
      }
    }
  };

  const handleCircleChange = (event) => {
    const selectedCircleCode = event.target.value
      ? parseInt(event.target.value, 10)
      : "";
    setCircleCode(selectedCircleCode);
    setSelectedDivision("");
    setSelectedSubDivision("");
    if (selectedCircleCode) {
      setOfficeCode(selectedCircleCode);
      const circle = filteredCircles.find(
        (item) => item.circleCode === selectedCircleCode,
      );
      const nameValue = circle?.circleName || "";
      setOfficeName(nameValue.concat(" Circle"));
      setCircleName(nameValue);
      const uniqueDivisions = subDivisions
        .filter((item) => item.circleCode == selectedCircleCode)
        .reduce((acc, item) => {
          if (!acc.some((division) => division.divisionCode === item.divisionCode)) {
            acc.push({
              divisionCode: item.divisionCode,
              divisionName: item.divisionName,
              companyCode: item.companyCode,
            });
          }
          return acc;
        }, []);
      setFilteredDivisions(uniqueDivisions);
    } else {
      setOfficeCode(selectedCompanyCode || offCode);
      setOfficeName(getCompanies(selectedCompanyCode) || getName(String(offCode), subDivisions));
      setFilteredDivisions([]);
      setFilteredSubDivisions([]);
    }
  };

  const handleDivisionChange = (event) => {
    const selectedDivisionCode = event.target.value
      ? parseInt(event.target.value, 10)
      : "";
    setSelectedDivision(selectedDivisionCode);
    setSelectedSubDivision("");
    if (selectedDivisionCode) {
      setOfficeCode(selectedDivisionCode);
      const divisionName =
        filteredDivisions.find(
          (division) => division.divisionCode === selectedDivisionCode,
        )?.divisionName || "";
      setOfficeName(divisionName.concat(" Division"));
      const uniqueSubDivisions = subDivisions
        .filter((item) => item.divisionCode == selectedDivisionCode)
        .reduce((acc, item) => {
          if (!acc.some((sub) => sub.subDivisionCode === item.subDivisionCode)) {
            acc.push({
              subDivisionCode: item.subDivisionCode,
              subDivisionName: item.subDivisionName,
              companyCode: item.companyCode,
            });
          }
          return acc;
        }, []);
      setFilteredSubDivisions(uniqueSubDivisions);
    } else {
      setOfficeCode(circleCode || offCode);
      setOfficeName(
        circleName
          ? circleName.concat(" Circle")
          : getName(String(offCode), subDivisions),
      );
      setFilteredSubDivisions([]);
    }
  };

  const handleSubDivisionChange = (event) => {
    const selectedSubDivisionCode = event.target.value
      ? parseInt(event.target.value, 10)
      : "";
    setSelectedSubDivision(selectedSubDivisionCode);
    if (selectedSubDivisionCode) {
      setOfficeCode(selectedSubDivisionCode);
      const subDivisionName =
        filteredSubDivisions.find(
          (sub) => sub.subDivisionCode === selectedSubDivisionCode,
        )?.subDivisionName || "";
      setOfficeName(subDivisionName.concat(" Sub Division"));
    } else {
      const divisionName =
        filteredDivisions.find(
          (division) => division.divisionCode === selectedDivision,
        )?.divisionName || "";
      setOfficeName(
        divisionName
          ? divisionName.concat(" Division")
          : getName(String(selectedDivision || offCode), subDivisions),
      );
      setOfficeCode(selectedDivision || circleCode || offCode);
    }
  };

  useEffect(() => {
    if (roleTypeName == "Technical") {
      if (offCode) {
        setOfficeCode(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode));
        setOfficeName(
          getName(
            String(resolveOfficeCodeForAdmin(offCode, selectedAdminCompanyCode)),
            subDivisions,
          ),
        );
        setHierarchy(offCode.toString().length);
      }
    } else if (roleTypeName == "Commercial") {
      if (cfcLevelId == 1 && offCode) {
        if (offCode == 10) {
          const effectiveCompany = getEffectiveAdminCompanyCode(
            offCode,
            selectedCompanyCode,
            selectedAdminCompanyCode,
          );
          setOfficeCode(effectiveCompany);
          setOfficeName(getCompanies(String(effectiveCompany)) || "PITC");
        } else {
          setOfficeCode(offCode);
          setOfficeName(getName(offCode, subDivisions));
        }
        setHierarchy(String(offCode).length);
      } else if (offCode && cfcLevelId >= 2 && cfcLevelId <= 5) {
        setOfficeCode(offCode.toString().slice(0, cfcLevelId));
        setOfficeName(cscID);
        setHierarchy(cfcLevelId);
      }
    }
  }, [offCode, cfcLevelId, roleTypeName, cscID, selectedAdminCompanyCode, selectedCompanyCode, subDivisions]);

  useEffect(() => {
    if (!hierarchy) return;
    if (hierarchy == 1 || cfcLevelId === 1) {
      const circles = subDivisions.reduce((acc, curr) => {
        if (!acc.some((item) => item.circleCode === curr.circleCode)) {
          acc.push({
            circleCode: curr.circleCode,
            circleName: curr.circleName,
            companyCode: curr.companyCode,
          });
        }
        return acc;
      }, []);
      setFilteredCircles(circles);
    } else if (hierarchy == 2 || cfcLevelId == 2) {
      const circles = subDivisions.reduce((acc, curr) => {
        if (!acc.some((item) => item.circleCode === curr.circleCode)) {
          acc.push({
            circleCode: curr.circleCode,
            circleName: curr.circleName,
            companyCode: curr.companyCode,
          });
        }
        return acc;
      }, []);
      setFilteredCircles(circles);
    } else if (hierarchy == 3 || cfcLevelId == 3) {
      setCircleCode(offCode.slice(0, 3));
      const uniqueDivisions = subDivisions
        .filter((item) => item.circleCode == offCode.slice(0, 3))
        .reduce((acc, item) => {
          if (!acc.some((division) => division.divisionCode === item.divisionCode)) {
            acc.push({
              divisionCode: item.divisionCode,
              divisionName: item.divisionName,
              companyCode: item.companyCode,
            });
          }
          return acc;
        }, []);
      setFilteredDivisions(uniqueDivisions);
    } else if (hierarchy == 4 || cfcLevelId == 4 || hierarchy == 5 || cfcLevelId == 5) {
      setSelectedDivision(offCode.slice(0, 4));
      const uniqueSubDivisions = subDivisions
        .filter((item) => item.divisionCode == offCode.slice(0, 4))
        .reduce((acc, item) => {
          if (!acc.some((sub) => sub.subDivisionCode === item.subDivisionCode)) {
            acc.push({
              subDivisionCode: item.subDivisionCode,
              subDivisionName: item.subDivisionName,
              companyCode: item.companyCode,
            });
          }
          return acc;
        }, []);
      setFilteredSubDivisions(uniqueSubDivisions);
    }
  }, [hierarchy, cfcLevelId, subDivisions, offCode]);

  const effectiveConnectionTypes = useMemo(
    () =>
      sortConnectionTypeRows(
        dedupeConnectionTypes([...DEFAULT_CONNECTION_TYPES, ...connectionTypes]),
      ),
    [connectionTypes],
  );

  const tableDisplayRows = useMemo(() => {
    if (displayRows.length > 0) {
      return rowDimension === "connectionType"
        ? sortConnectionTypeRows(displayRows)
        : displayRows;
    }

    return effectiveConnectionTypes.map((type) => ({
      key: connectionTypeKey(type),
      label: type,
      dimension: "connectionType",
    }));
  }, [displayRows, effectiveConnectionTypes, rowDimension]);

  const rowColumnLabel = ROW_DIMENSION_LABELS.connectionType;

  const totalsRow = useMemo(
    () => computeTotalsRow(tableData, tableDisplayRows),
    [tableData, tableDisplayRows],
  );

  const currentFilters = {
    dateFrom,
    dateTo,
    selectedCompanyCode: code === 10 ? selectedCompanyCode : null,
    circleCode,
    selectedDivision,
    selectedSubDivision,
    officeCode,
  };

  useEffect(() => {
    if (lastAppliedFilters === null) {
      setIsGoDisabled(false);
      return;
    }
    const hasChanged =
      JSON.stringify(currentFilters) !== JSON.stringify(lastAppliedFilters);
    setIsGoDisabled(!hasChanged);
  }, [
    dateFrom,
    dateTo,
    selectedCompanyCode,
    circleCode,
    selectedDivision,
    selectedSubDivision,
    officeCode,
    lastAppliedFilters,
  ]);

  const fetchReportData = async (reportOfficeCode, fromDate, toDate) => {
    setViewBackdrop(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        officeCode: String(reportOfficeCode),
        dateFrom: fromDate || "",
        dateTo: toDate || "",
        connectionType: "All",
      });

      const response = await fetch(
        cfpBaseUrl + "enc/getPerformanceEvaluationReport",
        {
          method: "POST",
          headers: myHeaders,
          body: raw,
          redirect: "follow",
        },
      );
      const result = await response.json();
      const reportRows = normalizeReportRows(result);

      if (isSuccessfulReportResponse(result)) {
        const typesFromData = dedupeConnectionTypes(
          reportRows
            .map((item) =>
              (item.connectionType || item.connectionTypeName)
                ? String(item.connectionType || item.connectionTypeName).trim()
                : null,
            )
            .filter(Boolean),
        );
        const mergedTypes = dedupeConnectionTypes([
          ...effectiveConnectionTypes,
          ...typesFromData,
        ]);
        if (mergedTypes.length) {
          setConnectionTypes(mergedTypes);
        }

        const mapped = mapApiDataToMatrix(
          reportRows,
          mergedTypes.length ? mergedTypes : effectiveConnectionTypes,
        );
        setTableData(mapped.matrix);
        setDisplayRows(mapped.displayRows);
        setRowDimension(mapped.rowDimension);
      } else {
        setTableData(buildEmptyMatrix(effectiveConnectionTypes));
        setDisplayRows([]);
        setRowDimension("connectionType");
      }
      setHasFetchedData(true);
    } catch (error) {
      setTableData(buildEmptyMatrix(effectiveConnectionTypes));
      setDisplayRows([]);
      setRowDimension("connectionType");
      setHasFetchedData(true);
    } finally {
      setViewBackdrop(false);
    }
  };

  const handleGoClick = () => {
    const reportOfficeCode = resolveReportGoOfficeCode({
      code,
      officeCode,
      selectedCompanyCode,
      selectedAdminCompanyCode,
      circleCode,
      selectedDivision,
      selectedSubDivision,
    });
    fetchReportData(reportOfficeCode, dateFrom, dateTo);
    setLastAppliedFilters({ ...currentFilters });
    setIsGoDisabled(true);
  };

  const reportBannerTitle = `${REPORT_TITLE} for (${officeName})`;
  const reportBannerDateRange =
    dateFrom && dateTo && convertDate(dateFrom) === convertDate(dateTo)
      ? `of (${convertDate(dateFrom)})`
      : `from ${dateFrom ? convertDate(dateFrom) : "-"} to ${
          dateTo ? convertDate(dateTo) : "-"
        }`;
  const reportBannerText = `${reportBannerTitle} ${reportBannerDateRange}`;

  const exportFilePrefix = "Performance-Evaluation-Report";

  const exportToExcel = () => {
    const wb = XLSX.utils.table_to_book(tableRef.current, { sheet: "Sheet1" });
    boldDomTheadRows(wb.Sheets[wb.SheetNames[0]], tableRef.current, 0);
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout]),
      `${officeName} - ${exportFilePrefix} - ${dateFrom} to ${dateTo}.xlsx`,
    );
  };

  const DownloadPdf = async (print) =>
    new Promise((resolve) => {
      const head = [
        [
          { content: rowColumnLabel, rowSpan: 3 },
          { content: "Applications Received = A+B+C+D", colSpan: 2, rowSpan: 2 },
          { content: "A = Un-Ripe", colSpan: 6 },
          { content: "B = Ripe", colSpan: 10 },
          {
            content: "C = Cancel Applications\nBefore DN Approval & Due to Non Payment of DN",
            colSpan: 2,
            rowSpan: 2,
          },
          { content: "D = Objected Applications", colSpan: 2, rowSpan: 2 },
        ],
        [
          { content: "Total", colSpan: 2 },
          { content: "Under Survey", colSpan: 2 },
          { content: "DN Issued (Payment Awaited)", colSpan: 2 },
          { content: "Total = X + Y", colSpan: 2 },
          { content: "X = Meters Installed Against Ripe Cases (X = E + F)", colSpan: 2 },
          { content: "Y = Pending Ripe (Meters Yet to Be Installed)", colSpan: 2 },
          { content: "E = Posted on IPS for Billing", colSpan: 2 },
          { content: "F = Delayed Billing", colSpan: 2 },
        ],
        REPORT_LEAF_KEYS.flatMap(() => ["No. of Applications", "Load (kW)"]),
      ];

      const body = tableDisplayRows.map(({ key, label }) => {
        const row = tableData[key] || {};
        return [
          label,
          ...REPORT_LEAF_KEYS.flatMap((leafKey) => [
            formatCount(row[leafKey]?.count),
            formatLoad(row[leafKey]?.load),
          ]),
        ];
      });

      body.push([
        "Total",
        ...REPORT_LEAF_KEYS.flatMap((leafKey) => [
          formatCount(totalsRow[leafKey]?.count),
          formatLoad(totalsRow[leafKey]?.load),
        ]),
      ]);

      const pdf = new jsPDF("landscape", "mm", "a4", { compressPdf: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      pdf.addImage(getLogo(companyCode), "PNG", 15, 5, 25, 20);
      pdf.addImage(eOpsLogo, "PNG", 43, 8, 20, 8);
      pdf.setFontSize(12);
      pdf.text(getCompanies(companyCode) || "", pageWidth / 2, 12, {
        align: "center",
      });
      pdf.setFontSize(10);
      pdf.text(reportBannerTitle, pageWidth / 2, 19, { align: "center" });
      pdf.text(reportBannerDateRange, pageWidth / 2, 24, { align: "center" });
      pdf.text(convertDatewithTime(new Date()), pageWidth - 65, 12);
      pdf.text("Name: " + name, pageWidth - 65, 17);
      pdf.text("Designation: " + roleName, pageWidth - 65, 22);

      pdf.autoTable({
        head,
        body,
        startY: 30,
        theme: "grid",
        styles: { fontSize: 5.5, halign: "right", valign: "middle", cellPadding: 1 },
        headStyles: {
          fillColor: [0, 106, 209],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          halign: "center",
          valign: "middle",
          lineWidth: 0.15,
          lineColor: [255, 255, 255],
        },
        columnStyles: {
          0: { halign: "left" },
        },
        didParseCell: (data) => {
          if (data.cell.section !== "body") return;
          if (data.column.index === 0) {
            data.cell.styles.halign = "left";
          } else {
            data.cell.styles.halign = "right";
          }
          if (data.row.index === body.length - 1) {
            data.cell.styles.fillColor = [254, 245, 192];
            data.cell.styles.fontStyle = "bold";
          }
        },
      });

      if (print) {
        openJsPdfInNewTab(
          pdf,
          `${officeName}- ${exportFilePrefix} - ${dateFrom} to ${dateTo}.pdf`,
        );
      }
      resolve(pdf);
    });

  const printPDF = async () => {
    const pdf = await DownloadPdf(false);
    const blobUrl = pdf.output("bloburl");
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = blobUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };
  };

  const noDataAvailable = () => {
    alert("No data available to export or print.");
  };

  const FilterFieldLabel = ({ htmlFor, children }) => (
    <Typography
      component="label"
      htmlFor={htmlFor}
      variant="caption"
      sx={{
        display: "block",
        fontWeight: 600,
        color: "text.secondary",
        lineHeight: 1.35,
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );

  const renderFilters = ({ showGo = false } = {}) => (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid item xs={12} sm={6} md={2}>
        <Stack spacing={0.5}>
          <FilterFieldLabel htmlFor="perf-eval-date-from">Date From</FilterFieldLabel>
          <TextField
            id="perf-eval-date-from"
            fullWidth
            size="small"
            variant="outlined"
            type="date"
            value={dateFrom}
            inputProps={getReportDateInputProps()}
            onChange={(e) => setDateFrom(clampIsoDateToToday(e.target.value))}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={6} md={2}>
        <Stack spacing={0.5}>
          <FilterFieldLabel htmlFor="perf-eval-date-to">Date To</FilterFieldLabel>
          <TextField
            id="perf-eval-date-to"
            fullWidth
            size="small"
            variant="outlined"
            type="date"
            value={dateTo}
            inputProps={getReportDateInputProps()}
            onChange={(e) => setDateTo(clampIsoDateToToday(e.target.value))}
          />
        </Stack>
      </Grid>
      {code == 10 && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor="perf-eval-company">Company</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                id="perf-eval-company"
                displayEmpty
                value={selectedCompanyCode || ""}
                onChange={handleChangeCompany}
              >
                <MenuItem value="">
                  <em>Select Company</em>
                </MenuItem>
                {getDiscoCompanyMenuItems(selectedCompanyCode)}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
      {hierarchy <= 2 && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor="perf-eval-circle">Circle</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                id="perf-eval-circle"
                displayEmpty
                value={circleCode === "" ? "" : circleCode}
                onChange={handleCircleChange}
                renderValue={(v) => renderCircleValue(v, filteredCircles)}
              >
                <MenuItem value="">
                  <em>All Circle</em>
                </MenuItem>
                {filteredCircles.map((circle) => (
                  <MenuItem key={circle.circleCode} value={circle.circleCode}>
                    {circle.circleName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
      {hierarchy <= 3 && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor="perf-eval-division">Division</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                id="perf-eval-division"
                displayEmpty
                value={selectedDivision === "" ? "" : selectedDivision}
                onChange={handleDivisionChange}
                disabled={!circleCode}
                renderValue={(v) => renderDivisionValue(v, filteredDivisions)}
              >
                <MenuItem value="">
                  <em>All Division</em>
                </MenuItem>
                {filteredDivisions.map((division) => (
                  <MenuItem key={division.divisionCode} value={division.divisionCode}>
                    {division.divisionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
      {hierarchy <= 4 && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5}>
            <FilterFieldLabel htmlFor="perf-eval-subdiv">Sub-Division</FilterFieldLabel>
            <FormControl fullWidth size="small" variant="outlined">
              <Select
                id="perf-eval-subdiv"
                displayEmpty
                value={selectedSubDivision === "" ? "" : selectedSubDivision}
                onChange={handleSubDivisionChange}
                disabled={!selectedDivision}
                renderValue={(v) => renderSubDivisionValue(v, filteredSubDivisions)}
              >
                <MenuItem value="">
                  <em>All Sub-Division</em>
                </MenuItem>
                {filteredSubDivisions.map((subDivision) => (
                  <MenuItem
                    key={subDivision.subDivisionCode}
                    value={subDivision.subDivisionCode}
                  >
                    {subDivision.subDivisionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      )}
      {showGo && (
        <Grid item xs={12} sm={6} md={2}>
          <Stack spacing={0.5} sx={{ pt: "2px" }}>
            <Box aria-hidden sx={{ height: "1.35rem", mb: 0.5, lineHeight: 1.35 }} />
            <Button
              id="perf-eval-go-btn"
              fullWidth
              variant="contained"
              disableElevation
              className="coa-go-btn"
              onClick={handleGoClick}
              disabled={isGoDisabled || viewBackdrop}
            >
              GO
            </Button>
          </Stack>
        </Grid>
      )}
    </Grid>
  );

  const isExportEmpty =
    !hasFetchedData || !matrixHasData(tableData, tableDisplayRows);

  const showNoDataRow = !matrixHasData(tableData, tableDisplayRows);

  usePerfEvalStickyHeaderHeight(tableRef, [tableData, viewBackdrop, hasFetchedData]);

  return (
    <>
      <Box className="form-box rpt-page coa-status-report perf-eval-summary-page">
        <div className="coa-layout">
          <header className="coa-page-header">
            <h1 className="coa-page-title">{REPORT_TITLE}</h1>
          </header>

          <div className="coa-toolbar-stack">
            <Paper
              variant="outlined"
              className="coa-card coa-card--filters"
              sx={{
                p: 2,
                width: "100%",
                bgcolor: "#fff",
                borderRadius: 2,
                borderColor: "divider",
              }}
            >
              <div
                className={
                  isMdUp ? "coa-filters coa-filters--mui-grid" : "coa-filters"
                }
              >
                {isMdUp ? (
                  renderFilters({ showGo: true })
                ) : (
                  <p className="coa-filters-hint">
                    Tap <strong>Filters</strong> to set dates, company, and office,
                    then tap <strong>GO</strong>.
                  </p>
                )}
              </div>

              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                spacing={1}
                className="coa-export-strip"
                sx={{
                  pt: 2,
                  mt: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  gap: 1,
                }}
              >
                {!isMdUp && (
                  <Tooltip title="Filters">
                    <IconButton
                      color="primary"
                      onClick={() => setMobileFilterOpen(true)}
                      aria-label="Open filters"
                      size="small"
                    >
                      <FilterListIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {!isMdUp && (
                  <Button
                    variant="contained"
                    disableElevation
                    className="coa-go-btn"
                    size="small"
                    onClick={handleGoClick}
                    disabled={isGoDisabled || viewBackdrop}
                  >
                    GO
                  </Button>
                )}
                <ReportExportButtons
                  isEmpty={isExportEmpty}
                  onEmpty={noDataAvailable}
                  onPdf={() => DownloadPdf(true)}
                  onExcel={exportToExcel}
                  onPrint={printPDF}
                />
              </Stack>
            </Paper>
          </div>

          <div className="coa-report-banner perf-eval-banner" role="status">
            {reportBannerText}
          </div>

          <Box
            className="application-table"
            sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
          >
            <div className="div-datagrid">
              <div className="reports-container">
                <div className="coa-table-panel">
                  <div className="coa-table-scroll" id="pdf-table">
                    <table className="perf-eval-matrix table" ref={tableRef}>
                      <thead>
                        <tr>
                          <th
                            rowSpan={3}
                            scope="col"
                            className="perf-eval-sticky-type"
                          >
                            {rowColumnLabel}
                          </th>
                          <th
                            colSpan={2}
                            rowSpan={2}
                            scope="colgroup"
                          >
                            Applications Received
                            <span className="perf-eval-th-eq">= A+B+C+D</span>
                          </th>
                          <th colSpan={6} scope="colgroup">
                            A = Un-Ripe
                          </th>
                          <th colSpan={10} scope="colgroup">
                            B = Ripe
                          </th>
                          <th
                            colSpan={2}
                            rowSpan={2}
                            scope="colgroup"
                          >
                            C = Cancel Applications
                            <span className="perf-eval-th-note">
                              Before DN Approval &amp; Due to Non Payment of DN
                            </span>
                          </th>
                          <th
                            colSpan={2}
                            rowSpan={2}
                            scope="colgroup"
                          >
                            D = Objected Applications
                          </th>
                        </tr>
                        <tr>
                          <th colSpan={2} scope="colgroup">
                            Total
                          </th>
                          <th colSpan={2} scope="colgroup">
                            Under Survey
                          </th>
                          <th colSpan={2} scope="colgroup">
                            DN Issued
                            <span className="perf-eval-th-note">
                              (Payment Awaited)
                            </span>
                          </th>
                          <th colSpan={2} scope="colgroup">
                            Total = X + Y
                          </th>
                          <th colSpan={2} scope="colgroup">
                            X = Meters Installed Against Ripe Cases
                            <span className="perf-eval-th-eq">X = E + F</span>
                          </th>
                          <th colSpan={2} scope="colgroup">
                            Y = Pending Ripe
                            <span className="perf-eval-th-note">
                              (Meters Yet to Be Installed)
                            </span>
                          </th>
                          <th colSpan={2} scope="colgroup">
                            E = Posted on IPS for Billing
                          </th>
                          <th colSpan={2} scope="colgroup">
                            F = Delayed Billing
                          </th>
                        </tr>
                        <tr>
                          {REPORT_LEAF_KEYS.map((leafKey) => (
                            <React.Fragment key={`${leafKey}-sub`}>
                              <th scope="col">No. of Applications</th>
                              <th scope="col">Load (kW)</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {showNoDataRow ? (
                          <tr className="coa-empty-row">
                            <td colSpan={PERF_EVAL_TABLE_COL_SPAN}>No Data Found</td>
                          </tr>
                        ) : (
                          <>
                            {tableDisplayRows.map(({ key, label }) => {
                              const row = tableData[key] || {};
                              return (
                                <tr key={key}>
                                  <td className="perf-eval-sticky-type">{label}</td>
                                  {REPORT_LEAF_KEYS.map((leafKey) => (
                                    <React.Fragment key={`${key}-${leafKey}`}>
                                      <td className="perf-eval-num">
                                        {formatCount(row[leafKey]?.count)}
                                      </td>
                                      <td className="perf-eval-num">
                                        {formatLoad(row[leafKey]?.load)}
                                      </td>
                                    </React.Fragment>
                                  ))}
                                </tr>
                              );
                            })}
                            <tr className="perf-eval-matrix__total-row">
                              <td className="perf-eval-sticky-type">Total</td>
                              {REPORT_LEAF_KEYS.map((leafKey) => (
                                <React.Fragment key={`total-${leafKey}`}>
                                  <td className="perf-eval-num">
                                    {formatCount(totalsRow[leafKey]?.count)}
                                  </td>
                                  <td className="perf-eval-num">
                                    {formatLoad(totalsRow[leafKey]?.load)}
                                  </td>
                                </React.Fragment>
                              ))}
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </Box>

          <Drawer
            anchor="left"
            open={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            PaperProps={{
              className: "coa-filter-drawer",
              sx: {
                width: { xs: "100%", sm: 400 },
                maxWidth: "100vw",
                boxSizing: "border-box",
              },
            }}
          >
            <Box className="coa-drawer-inner" sx={{ p: 2, pb: 3, boxSizing: "border-box" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6" component="div">
                  Filters
                </Typography>
                <IconButton
                  aria-label="Close filters"
                  onClick={() => setMobileFilterOpen(false)}
                  edge="end"
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
              {renderFilters({ showGo: true })}
            </Box>
          </Drawer>
        </div>
      </Box>

      <Backdrop
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1000 }}
        open={viewBackdrop}
      >
        <AppLoader />
      </Backdrop>
    </>
  );
};

export default PerformanceEvaluationReport;
